// Plain-language layer (2026-07). The app's role names and directory concepts
// are IGA/AD jargon that the lowest-literacy operators can't parse. Rather than
// rename the underlying roles (they map to Entra security groups and the spec),
// we keep the names and attach a friendly "what this lets you do" line wherever
// a role or status is shown. One place, so the wording stays consistent.

import type { AppRole } from '@/stores/auth';

// Friendly, spaced display name for a role (the stored ids are PascalCase) plus
// a one-sentence description of what the holder can actually do. The two most
// similar-sounding roles - Profile Manager and Profile Admin - do opposite jobs,
// so the descriptions lead with the verb to make the difference obvious.
export const ROLE_INFO: Record<AppRole, { label: string; can: string }> = {
  Admin: {
    label: 'Admin',
    can: 'Full control of the app, every account, and all settings.',
  },
  ProfileAdmin: {
    label: 'Profile Admin',
    can: 'Can add, update and remove people in the account types they look after.',
  },
  ProfileManager: {
    label: 'Profile Manager',
    can: 'Can design account types (the templates people are created from). Does not add, change or remove people.',
  },
  Auditor: {
    label: 'Auditor',
    can: 'Can look at accounts and the activity log. Cannot make changes.',
  },
  HelpDesk: {
    label: 'Help Desk',
    can: 'Can look up people and give out new sign-in codes. Does not add, change or remove people.',
  },
};

export function roleLabel(role: AppRole): string {
  return ROLE_INFO[role]?.label ?? role;
}

export function roleDescription(role: AppRole): string {
  return ROLE_INFO[role]?.can ?? '';
}

// Whether a set of roles can add / update / remove people (run the Joiner /
// Mover / Leaver flows). This is the "operator" capability the Simple mode is
// built for. Mirrors the backend writers = ['Admin','ProfileAdmin'].
export function canOperateAccounts(roles: AppRole[]): boolean {
  return roles.includes('Admin') || roles.includes('ProfileAdmin');
}

// Someone whose only role is Profile Manager lands in a shell with no way to
// manage people and any /users link bounces to Forbidden. Detect that so the UI
// can explain it in plain language instead of a silent dead-end.
export function isTemplateAuthorOnly(roles: AppRole[]): boolean {
  return (
    roles.includes('ProfileManager') &&
    !roles.includes('Admin') &&
    !roles.includes('ProfileAdmin')
  );
}

// Plain status for a person in the people list, replacing the "app-managed /
// eligible / not eligible" jargon. Tone drives the badge colour.
export type PersonStatusTone = 'managed' | 'addable' | 'other';

export function personStatus(row: {
  managedByApp: boolean;
  eligible: boolean;
}): { label: string; help: string; tone: PersonStatusTone } {
  if (row.managedByApp) {
    return {
      label: 'Managed here',
      help: 'This app looks after this account - you can update or remove it.',
      tone: 'managed',
    };
  }
  if (row.eligible) {
    return {
      label: 'Can be added',
      help: "This person exists in the directory but isn't set up here yet. Add them to start managing their account.",
      tone: 'addable',
    };
  }
  return {
    label: 'Not managed here',
    help: 'This account exists in the directory but is outside the account types you look after.',
    tone: 'other',
  };
}

// One-time sign-in credential the joiner/reissue hands back, translated into
// plain hand-over guidance. The kinds map to the profile's auth method:
//  - tap: a Temporary Access Pass (one-time code, expires)
//  - password: an initial password (changed at first sign-in)
//  - qr: a QR code + PIN printed on a card
export type CredentialKind = 'tap' | 'password' | 'qr';

export function credentialTitle(kind: CredentialKind): string {
  switch (kind) {
    case 'tap':
      return 'One-time sign-in code';
    case 'password':
      return 'First password';
    case 'qr':
      return 'Sign-in card (QR code + PIN)';
  }
}

export function credentialInstruction(kind: CredentialKind, minutes?: number): string {
  switch (kind) {
    case 'tap':
      return minutes
        ? `Give this code to the person to sign in the first time. It works once and expires in ${minutes} minutes. It won't be shown again.`
        : "Give this code to the person to sign in the first time. It works once. It won't be shown again.";
    case 'password':
      return 'Give this password to the person securely. They will be asked to set their own the first time they sign in.';
    case 'qr':
      return 'Print or hand over the card. The person scans the QR code and enters the PIN to sign in the first time. Keep the PIN separate from the card.';
  }
}
